import React, { type ReactNode } from 'react'
import { useDrag, useDrop } from 'react-dnd'

interface DragAndDropItemProps {
  index: number
  updating: boolean
  disableDrag: boolean
  makeGray: boolean
  startMoveCard: () => void
  resetPositions: () => void
  moveCard: (dragIndex: number, hoverIndex: number) => void
  dropCard: () => void
  stackId: string
  setIsDragging: (isDragging: boolean) => void
  children: ReactNode
}

const ItemTypes = {
  CARD: 'card'
}

export const DragAndDropItem: React.FC<DragAndDropItemProps> = ({ index, updating, disableDrag, makeGray, startMoveCard, resetPositions, moveCard, dropCard, stackId, setIsDragging, children }) => {
  const ref = React.useRef<HTMLDivElement>(null)

  const [, drop] = useDrop({
    accept: ItemTypes.CARD,
    hover (item: { index: number, stackId: string }, monitor: { getClientOffset: () => any }) {
      if (ref.current == null || item.stackId !== stackId) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = (clientOffset).y - hoverBoundingRect.top
      if (isDragAboveHover(dragIndex, hoverIndex, hoverClientY, hoverMiddleY)) {
        return
      }
      if (isDragBelowHover(dragIndex, hoverIndex, hoverClientY, hoverMiddleY)) {
        return
      }
      moveCard(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  function isDragAboveHover (
    dragIndex: number,
    hoverIndex: number,
    hoverClientY: number | undefined,
    hoverMiddleY: number
  ): boolean {
    return dragIndex < hoverIndex && hoverClientY !== undefined && hoverClientY < hoverMiddleY
  }

  function isDragBelowHover (
    dragIndex: number,
    hoverIndex: number,
    hoverClientY: number | undefined,
    hoverMiddleY: number
  ): boolean {
    return dragIndex > hoverIndex && hoverClientY !== undefined && hoverClientY > hoverMiddleY
  }

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    canDrag: !updating && !disableDrag,
    item: () => {
      startMoveCard()
      setIsDragging(true)
      return { index, stackId }
    },
    end: (item, monitor) => {
      monitor.didDrop() ? dropCard() : resetPositions()
      setIsDragging(false)
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const opacity = isDragging ? 0.5 : (updating || makeGray ? 0.5 : 1)
  const transition = 'transform 2s ease' // Define the transition property
  drag(drop(ref))

  return (<div key={index} ref={ref} style={{ opacity, transition, cursor: updating || disableDrag ? 'not-allowed' : 'grab' }}>
    {children}

  </div>)
}
